/**
 * "Request this unit" — point 14's UOM half, placed where the need actually arises.
 *
 * A line whose unit the UOM master rejects already says so, and already offers the base unit as a
 * way out. That is the right escape when the base unit will do; it is the wrong one when the
 * customer genuinely orders in DRUMS and the master simply has no DRUMS. Until now the purchaser's
 * only option there was to quote in something the customer did not ask for.
 *
 * ---- It files a REQUEST, and says so at every step ----
 *
 * `items.uom_creation_request` creates a `UOM Creation Request` document for someone to approve.
 * The master is unchanged, so the line STILL cannot be set to that unit afterwards. Every string
 * here is careful about that: a purchaser who reads "created" will go back to the row, fail to
 * find the unit, and conclude the app is broken.
 *
 * Once filed it reports the request and stops offering — filing the same one twice helps nobody,
 * and the server does not deduplicate.
 */

import { useState } from 'react'
import { useToast } from '../../components/Toast'
import { useRequestUom, uomRequestProblem } from '../../lib/api/uomRequest'
import { useUomMaster } from '../../lib/api/useUoms'
import { useApprovedUomRequests } from '../../lib/api/uomSync'

export function RequestUomLink({
  uom,
  enquiryId,
  rowId,
  itemCode,
  itemUnits = null,
  disabled,
}: {
  uom: string
  /** The enquiry, line serial and item the unit is wanted for — required by the server since
   *  2026-09-19 (`reference_name` / `row_id` / `item`). */
  enquiryId: string
  rowId: string
  itemCode: string | null
  /** The matched item's own units (base + conversions), or `null` when the line has no item.
   *  With an item, a unit the master has but the item lacks is still requestable — see
   *  `uomRequestProblem`. */
  itemUnits?: readonly string[] | null
  disabled?: boolean
}) {
  const toast = useToast()
  /*
    The duplicate guard reads the GLOBAL UOM master, not the item's own conversions.

    "Does this unit exist?" is a question about the master; whether a particular item converts to it
    is a different question with a different answer. Guarding on the item's list would let a
    purchaser file a request for `CTN` because THIS item has no carton conversion, when `CTN` has
    existed all along. Cached and shared — the extraction path already loads it.
  */
  const uomMaster = useUomMaster()
  const [requested, setRequested] = useState<string | null>(null)
  // Approval happens elsewhere; the page polls for it and pulls the unit onto the item
  // (`uomSync.ts`). "Check now" is the same poll, on demand, for the purchaser who was just told.
  const approved = useApprovedUomRequests(enquiryId)

  const request = useRequestUom({
    onSuccess: (name, requestedUom) => {
      setRequested(name)
      // "Requested", never "created" — see the header.
      toast(`Requested “${requestedUom}” — a UOM creation request was filed for approval.`)
    },
  })

  if (requested) {
    return (
      <span style={{ color: 'var(--muted)' }}>
        · requested <span className="mono">{requested}</span> — awaiting approval ·{' '}
        <button type="button" className="linkish" disabled={approved.isFetching} onClick={() => void approved.refetch()}>
          {approved.isFetching ? 'checking…' : 'check now'}
        </button>
      </span>
    )
  }

  // Until the master has loaded, offering nothing is the safe direction: the duplicate check is
  // the only thing standing between a typo and a junk approval queue.
  if (!uomMaster.data) return null

  const problem = uomRequestProblem(uom, uomMaster.data, itemUnits)
  // Nothing to offer when the unit is blank or the master already has it. Silent rather than a
  // disabled control: this sits inside a warning line that is already doing enough talking.
  if (problem !== null) return null

  return (
    <span
      className="linkish"
      style={disabled || request.isPending ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
      title={
        itemUnits !== null && uomMaster.data.some((u) => u.trim().toLowerCase() === uom.trim().toLowerCase())
          ? `File a request for “${uom}” to be added to this item. Once approved it appears in the line's unit switcher.`
          : `File a request for “${uom}” to be added to the UOM master. It will not be usable until someone approves it.`
      }
      onClick={() => {
        if (disabled || request.isPending) return
        request.mutate({ uom, enquiryId, rowId, itemCode })
      }}
    >
      {request.isPending ? 'requesting…' : `request “${uom}”`}
    </span>
  )
}
